// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import type { Styles } from 'shared/types';
import Gradient from './Gradient';
import { SLIDE_MIN_HEIGHT } from '../Slider';

type Props = {
  enableGradient?: Boolean,
  defaultImg: string,
  mobileImg: string,
  imageAlt?: String,
  responsiveImgs: string | {
    mobile: string,
    tablet: string,
    desktop: string
  }
}

export default function Image(props: Props) {
  const { defaultImg, mobileImg, responsiveImgs, enableGradient, imageAlt } = props;

  const c: Styles<typeof styles> = useStyles(props);

  if (!defaultImg && !mobileImg && !responsiveImgs) {
    return null;
  }

  // Responsive image set
  if (responsiveImgs?.desktop) {
    return (
      <>
        <picture>
          <source media="(max-width: 600px)" srcSet={responsiveImgs.mobile} />
          <source media="(max-width: 900px)" srcSet={responsiveImgs.tablet} />
          <source media="(min-width: 901px)" srcSet={responsiveImgs.desktop} />
          <img
            className={c.image}
            src={responsiveImgs.desktop}
            alt={imageAlt || 'Faraday Future'}
          />
        </picture>
        {enableGradient && <Gradient />}
      </>
    );
  }

  // Single image
  if (typeof responsiveImgs === 'string' && responsiveImgs.length > 0) {
    return (
      <>
        <picture>
          <source srcSet={responsiveImgs} />
          <img
            className={c.image}
            src={responsiveImgs}
            alt={imageAlt || 'Faraday Future'}
          />
        </picture>
        {enableGradient && <Gradient />}
      </>
    );
  }

  // Legacy - mobile/desktop image
  // TODO: Remove later
  if (defaultImg || mobileImg) {
    return (
      <>
        <picture>
          <source media="(max-width: 599px)" srcSet={mobileImg} />
          <source media="(min-width: 600px) and (orientation:portrait)" srcSet={mobileImg} />
          <source media="(min-width: 600px)" srcSet={defaultImg} />
          <img
            className={c.image}
            src={defaultImg}
            alt={imageAlt || 'Faraday Future'}
          />
        </picture>
        {enableGradient && <Gradient />}
      </>
    );
  }

  // No background image
  return null;
}

const styles = {
  image: {
    width: '100%',
    // use doc-height from product slide show if exists, to match the height of product slide show
    height: `var(--doc-height, max(100vh, ${SLIDE_MIN_HEIGHT}px))`,
    position: 'relative',
    overflow: 'hidden',
    objectFit: 'cover',
  },
};
const useStyles = createUseStyles(styles);
